import React, { useState, useEffect } from "react";
import { Phone, Calendar, MessageSquare, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { StatusBadge } from "./StatusBadge";
import { apiRequest, formatDateTime } from "../../lib/utils";

export const CallHistoryModal = ({
  open,
  onClose,
  contactId,
  contactType, // 'lead' or 'customer'
  contactName
}) => {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && contactId) {
      fetchCallHistory();
    }
  }, [open, contactId]);

  const fetchCallHistory = async () => {
    setLoading(true);
    try {
      const logs = await apiRequest(`/call-logs/history/${contactType}/${contactId}`);
      setCallLogs(logs);
    } catch (error) {
      console.error("Failed to fetch call history:", error);
    } finally {
      setLoading(false);
    }
  };

  const getOutcomeStyle = (outcome) => {
    switch (outcome) {
      case "answered":
        return "bg-green-100 text-green-800";
      case "no_answer":
        return "bg-yellow-100 text-yellow-800";
      case "busy":
        return "bg-orange-100 text-orange-800";
      case "voicemail":
        return "bg-blue-100 text-blue-800";
      case "wrong_number":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="call-history-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Call History - {contactName}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : callLogs.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No call history found
            </div>
          ) : (
            <div className="space-y-4">
              {callLogs.map((log) => (
                <div
                  key={log.id}
                  className="p-4 border rounded-lg bg-card"
                  data-testid={`call-log-${log.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getOutcomeStyle(
                          log.outcome
                        )}`}
                      >
                        {log.outcome?.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">
                      {formatDateTime(log.created_at)}
                    </span>
                  </div>

                  {log.remark && (
                    <div className="flex items-start gap-2 mt-2 text-sm">
                      <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <p className="text-foreground">{log.remark}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    {log.follow_up_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Follow-up: {formatDateTime(log.follow_up_date)}
                      </div>
                    )}
                    {log.booking_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Booking: {formatDateTime(log.booking_date)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CallHistoryModal;
